import React, { useEffect, useState, useRef, useMemo } from 'react';
import Draggable from 'react-draggable';
import { OmniDropdownComponent, OmniIconComponent, OmniStyleComponent, OmniTooltipNextComponent, Utils } from '../../../../../shared';

export const MediaOrderDetailsFilter = ({
    channelsListForFilter,
    suppliersListForFilter,
    clientIdsListForFilter,
    productIdsListForFilter,
    onApplyFilter,
    clearFilters,
    gridApi,
}) => {
    const nodeRef = useRef(null);

    const [displayFilters, setDisplayFilters] = useState(true);
    const [isMenuActive, setIsMenuActive] = useState(false);
    const [columns, setColumns] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ channels: '', suppliers: '', clientCode: '', productCode: '', });

    const clearAllFilters = () => {
        setFilterOptions({ channels: '', suppliers: '', clientCode: '', productCode: '', });
        toggleMenu(new Event('click'));
        clearFilters();
    }

    // Filter suppliers based on selected channel
    const filteredSuppliersForFilter = useMemo(() => {
        if (!filterOptions.channels) {
            return suppliersListForFilter;
        }
        return suppliersListForFilter.filter(
            supplier => supplier.channel === filterOptions.channels
        );
    }, [suppliersListForFilter, filterOptions.channels]);

    useEffect(() => {
        loadColumns();
    }, [gridApi]);

    const changeTab = (event) => {
        const tab = event.target.innerText;
        if (tab === "Filters") {
            setDisplayFilters(true);
        } else if (tab === "Columns") {
            setDisplayFilters(false);
        }
    }

    const toggleMenu = (event) => {
        event.stopPropagation();
        event.preventDefault();
        document.getElementById("dropdown-menu-content").classList.toggle("show");
        const isMenuActive = document.getElementById("dropdown-menu-content")?.classList.contains("show") ? true : false;
        if (isMenuActive) {
            document.querySelectorAll("om2-dropdown-impl").forEach(eachEle => {
                eachEle.shadowRoot.querySelector(".dropdown-content").style.position = "initial";
            });
        }
        setIsMenuActive(isMenuActive);
    }

    const handleViewAllToggle = () => {
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs() || [];
        const allLeafCols = getAllLeafCols(colDefs);

        const allVisible = allLeafCols.every((c) => !c.hide);
        // if all visible → hide all, else → show all
        gridApi.setColumnsVisible(
            allLeafCols.map((c) => c.colId),
            !allVisible
        );

        loadColumns();
    };

    const getAllLeafCols = (nodes) => {
        let result = [];
        nodes.forEach((node) => {
            if (node.group || node.children) {
                result = result.concat(getAllLeafCols(node.children));
            } else {
                result.push(node);
            }
        });
        return result;
    };

    const loadColumns = () => {
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs();

        const buildTree = (defs, parentKey = "") =>
            defs.map((def, idx) => {
                const key = `${parentKey}${def.headerName || def.field || def.colId}-${idx}`;
                if (def.children) {
                    return {
                        key,
                        group: true,
                        headerName: def.headerName,
                        children: buildTree(def.children, key + "-")
                    };
                } else {
                    return {
                        key,
                        group: false,
                        colId: def.colId,
                        headerName: def.headerName || def.field,
                        visible: !def.hide
                    };
                }
            });

        setColumns(buildTree(colDefs || []));
    };

    const handleGroupToggle = (group) => {
        if (!gridApi) return;

        const allChildren = getAllLeafCols(group.children);
        const allVisible = allChildren.every((c) => c.visible);

        gridApi.setColumnsVisible(
            allChildren.map((c) => c.colId),
            !allVisible
        );

        loadColumns();
    };

    const handleToggle = (colId) => {
        if (!gridApi) {
            console.error("gridApi is not available");
            return;
        }
        const columnDefs = gridApi.getColumnDefs() || [];
        let colDef = columnDefs.find((c) => c.colId === colId);
        if (!colDef) {
            // search for coldef in children
            const searchInChildren = (children) => {
                for (let child of children) {
                    if (child.colId === colId) return child;
                    if (child.children) {
                        const found = searchInChildren(child.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            colDef = searchInChildren(columnDefs);
            if (!colDef) {
                console.warn(`Column with colId ${colId} not found in any children`);
                return;
            }
        }
        const currentlyVisible = !colDef.hide;
        gridApi.setColumnsVisible([colId], !currentlyVisible);
        loadColumns();
    };

    const renderTree = (nodes, isRoot = false) =>
        nodes.map((node, index) =>
            node.group ? (
                <div key={node.key} className="column-group">
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            ref={(el) => {
                                if (el) {
                                    const allChildren = getAllLeafCols(node.children);
                                    const allVisible = allChildren.every((c) => c.visible);
                                    const noneVisible = allChildren.every((c) => !c.visible);

                                    el.checked = allVisible;
                                    el.indeterminate = !allVisible && !noneVisible;
                                }
                            }}
                            onChange={() => handleGroupToggle(node)}
                        />
                        {node.headerName}
                    </label>
                    <div className="group-children" style={{ marginLeft: "16px" }}>
                        {renderTree(node.children, false)}
                    </div>
                </div>
            ) : (
                <div key={node.key} className={`column-item ${index === 0 && isRoot ? 'first-column-item' : ''}`}>
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            checked={node.visible}
                            onChange={() => handleToggle(node.colId)}
                        />
                        {node.headerName}
                    </label>
                </div>
            )
        );

    const onDataChange = (type, event) => {
        let value = Utils.getValue(event);
        // todo: date formatting if needed
        // if (type === "startDate" || type === "endDate") {
        //     value = Utils.formatDateObject(value);
        // }
        
        updateFilterOptions(type, value);
    }

    const updateFilterOptions = (type, value) => {
        setFilterOptions(prevFilterOptions => {
            const newOptions = {
                ...prevFilterOptions,
                [type]: value
            };
            // Clear supplier selection when channel changes
            if (type === 'channels') {
                newOptions.suppliers = '';
            }
            return newOptions;
        });
    }

    const applyFilter = () => {
        onApplyFilter(filterOptions);
        toggleMenu(new Event('click'));
    }

    const allLeafCols = getAllLeafCols(columns);
    const allVisible = allLeafCols.every((c) => c.visible);
    const noneVisible = allLeafCols.every((c) => !c.visible);
    const isNull = (item) => !item
    const disableApplyFilter = (Object.values(filterOptions).every(isNull));  // If every value is null, disable the apply button


    return (
        <div className="media-order-details-filter">
                <div className={`menu-trigger ag-grid-filter-wrapper filter-container media-order-filter-container ${isMenuActive ? "bg-blue" : ""}`} >
                    <OmniTooltipNextComponent className="aggrid-filter-menu-trigger" onClick={toggleMenu}>
                        <button slot="invoker" aria-haspopup="true" className={`icon`}>
                            <OmniIconComponent className="is-size-2 funnel-icon" icon-id="omni:interactive:funnel" onClick={() => {}} />
                        </button>
                        <div slot="content">Filters</div>
                    </OmniTooltipNextComponent>

                    <Draggable nodeRef={nodeRef} axis="both" bounds='body' defaultPosition={{ x: 0, y: 0 }}>
                        <div ref={nodeRef} className='aggrid-filter-panel media-order-filter-panel'>

                            <div className="dropdown-menu-content campaign-filter-container aggrid-filter-container media-order-filter-container" id="dropdown-menu-content" role="menu">
                                <div>

                                    <div className="filter-header">
                                        <div>
                                            <button className={'button' + (displayFilters ? "" : " is-text")} onClick={event => changeTab(event)}>Filters</button>
                                            <button className={'button' + (displayFilters ? " is-text" : "")} onClick={event => changeTab(event)}>Columns</button>
                                        </div>
                                        <div className='filter-close-icon'>
                                            <OmniIconComponent onClick={toggleMenu} className="is-size-2 has-text-almost-black" icon-id="omni:interactive:close"></OmniIconComponent>
                                        </div>
                                    </div>

                                    <hr className='filter-header-bar' />
                                    {displayFilters ?

                                        <div className="filter-sections">
                                            <div className='filter-sections-list'>

                                                <section className="section-item media-direction-column side-section">
                                                    <label>
                                                        <div className="media-flex media-justify-space-between is-7 filter-left-box filter-label">
                                                            <span className="filter-label-text">Start date</span>
                                                        </div>
                                                        <div className="full-width">
                                                            <input
                                                                type="date"
                                                                data-date={filterOptions.startDate || "MM DD,YYYY"}
                                                                value={filterOptions.startDate || ''}
                                                                onChange={(e) => onDataChange('startDate', e)}
                                                                className="input date is-large filter-field input-with-icon"
                                                            />
                                                        </div>
                                                    </label>
                                                    <label>
                                                        <div className="media-flex media-justify-space-between filter-right-box is-7 filter-label">
                                                            <span className="filter-label-text">End date</span>
                                                        </div>
                                                        <div className="full-width">
                                                            <input
                                                                type="date"
                                                                data-date={filterOptions.endDate || "MM DD,YYYY"}
                                                                value={filterOptions.endDate || ''}
                                                                onChange={(e) => onDataChange('endDate', e)}
                                                                className="input date is-large filter-field input-with-icon"
                                                            />
                                                        </div>
                                                    </label>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Channels</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={channelsListForFilter}
                                                            value={filterOptions.channels ? [filterOptions.channels] : []}
                                                            onDropdownChange={(event) => onDataChange("channels", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Suppliers</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={filteredSuppliersForFilter}
                                                            value={filterOptions.suppliers ? [filterOptions.suppliers] : []}
                                                            onDropdownChange={(event) => onDataChange("suppliers", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Client code</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={clientIdsListForFilter}
                                                            value={filterOptions.clientCode ? [filterOptions.clientCode] : []}
                                                            onDropdownChange={(event) => onDataChange("clientCode", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Product code</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={productIdsListForFilter}
                                                            value={filterOptions.productCode ? [filterOptions.productCode] : []}
                                                            onDropdownChange={(event) => onDataChange("productCode", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column last-section-item">
                                                    <div className="action-btns">
                                                        <a
                                                            className={`clear-all-link ${disableApplyFilter ? 'disabled' : ''}`}
                                                            onClick={(e) => {
                                                                if (disableApplyFilter) {
                                                                    e.preventDefault();
                                                                    return;
                                                                }
                                                                clearAllFilters();
                                                            }}
                                                        >Clear all</a>
                                                        <button
                                                            className={`${disableApplyFilter ? 'secondary' : ''}`}
                                                            disabled={disableApplyFilter}
                                                            onClick={applyFilter}
                                                        >Done</button>
                                                    </div>
                                                </section>

                                            </div>
                                        </div>
                                        :
                                        <div className="filter-sections">
                                            <div className="view-all">
                                                <label>
                                                    <input type="checkbox" className="toggle-checkbox" onChange={handleViewAllToggle}
                                                        ref={(el) => {
                                                            if (el) {
                                                                el.checked = allVisible;
                                                                el.indeterminate = !allVisible && !noneVisible;
                                                            }
                                                        }} />
                                                    View All Columns
                                                </label>
                                            </div>

                                            <hr className="columns-separator" />
                                            {renderTree(columns, true)}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </Draggable>
                </div>
        </div>
    );
};

export default MediaOrderDetailsFilter;